import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface TokenStats {
  total: {
    total_requests: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_tokens: number;
    total_cost_usd: number;
  };
  byPromptType: Array<{
    prompt_type: string;
    requests_count: number;
    total_tokens: number;
    avg_tokens_per_request: number;
    total_cost: number;
  }>;
  byUser: Array<{
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    requests_count: number;
    total_tokens: number;
    avg_tokens_per_request: number;
    total_cost: number;
  }>;
}

interface ParserStats {
  total: {
    total_requests: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_tokens: number;
    total_cost_usd: number;
    avg_tokens_per_request: number;
    avg_cost_per_request: number;
  };
  bySource: Array<{
    source: string;
    requests_count: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_tokens: number;
    avg_tokens_per_request: number;
    total_cost_usd: number;
  }>;
  byPromptType: Array<{
    prompt_type: string;
    requests_count: number;
    total_tokens: number;
    avg_tokens_per_request: number;
    total_cost_usd: number;
  }>;
  timeSeries: Array<{
    date: string;
    requests_count: number;
    total_tokens: number;
    cost_usd: number;
  }>;
  hourly: Array<{
    hour: string;
    requests_count: number;
    total_tokens: number;
    cost_usd: number;
  }>;
  monthlyProjection: {
    avg_daily_cost: number;
    projected_monthly_cost: number;
    avg_daily_tokens: number;
    avg_daily_requests: number;
  };
  reasoningStats: Array<{
    source: string;
    total_reasoning_tokens: number;
    wasted_requests: number;
    wasted_tokens: number;
    wasted_cost: number;
  }>;
  reasoningSummary: {
    total_reasoning_tokens: number;
    total_all_tokens: number;
    total_requests: number;
    length_cutoff_requests: number;
    wasted_tokens: number;
    wasted_cost_rub: number;
  };
  lastVacancies: Array<{
    source: string;
    total_vacancies: number;
    last_vacancy_at: string;
  }>;
  availableModels: Array<{
    model: string;
    requests: number;
    total_tokens?: number;
    total_cost_usd?: number;
    first_used: string;
    last_used: string;
  }>;
}

interface SourceStatusItem {
  source: string;
  status: 'ok' | 'warning' | 'error' | 'pending';
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  lastErrorType: string | null;
  successCount: number;
  errorCount: number;
  vacanciesSaved: number;
  vacanciesFiltered: number;
  totalRequests: number;
}

interface SourceStatusResponse {
  sources: SourceStatusItem[];
  summary: { total: number; ok: number; warning: number; error: number; pending: number };
}

const PIE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];


const formatNum = (v: number | string | null | undefined) => Number(v || 0).toLocaleString();
const formatRub = (v: number | string | null | undefined) => `${Number(v || 0).toFixed(2)} \u20BD`;
const formatRubPrecise = (v: number | string | null | undefined) => `${Number(v || 0).toFixed(4)} \u20BD`;
const TOKEN_PRICE_LABEL = '250k = 15\u20BD';

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string; border: string }> = {
  ok: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', border: 'border-green-200' },
  warning: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500', border: 'border-yellow-200' },
  error: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', border: 'border-red-200' },
  pending: { bg: 'bg-gray-50', text: 'text-gray-500', dot: 'bg-gray-400', border: 'border-gray-200' },
};

const AITokenAnalyticsPage: React.FC = () => {
  const [stats, setStats] = useState<TokenStats | null>(null);
  const [parserStats, setParserStats] = useState<ParserStats | null>(null);
  const [sourceStatus, setSourceStatus] = useState<SourceStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [parserLoading, setParserLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeTab, setActiveTab] = useState<'user' | 'parser' | 'prompts'>('parser');
  const [hoveredSource, setHoveredSource] = useState<string | null>(null);
  const [modelFilter, setModelFilter] = useState<string>('all');

  // Prompts state
  const [extractionPrompt, setExtractionPrompt] = useState('');
  const [classificationPrompt, setClassificationPrompt] = useState('');
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsSaving, setPromptsSaving] = useState(false);
  const [promptsCustom, setPromptsCustom] = useState<{ extraction: boolean; classification: boolean }>({ extraction: false, classification: false });

  useEffect(() => {
    fetchStats();
    fetchParserStats();
    fetchSourceStatus();
    fetchPrompts();
    // Auto-refresh source status every 30 seconds
    const interval = setInterval(fetchSourceStatus, 30000);
    return () => clearInterval(interval);
  }, [startDate, endDate, modelFilter]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(`/admin/ai-token-usage/stats?${params}`);
      setStats(response.data.stats);
    } catch (error: any) {
      console.error('Ошибка загрузки статистики:', error);
      toast.error(error.response?.data?.message || 'Ошибка загрузки статистики токенов');
    } finally {
      setLoading(false);
    }
  };

  const fetchSourceStatus = async () => {
    try {
      const response = await api.get('/admin/ai-token-usage/parser-source-status');
      setSourceStatus(response.data);
    } catch (error: any) {
      console.error('Ошибка загрузки parser source status:', error);
    }
  };

  const fetchPrompts = async () => {
    try {
      setPromptsLoading(true);
      const response = await api.get('/admin/ai-token-usage/parser-prompts');
      if (response.data.success) {
        setExtractionPrompt(response.data.prompts.extraction || '');
        setClassificationPrompt(response.data.prompts.classification || '');
        setPromptsCustom(response.data.isCustom || { extraction: false, classification: false });
      }
    } catch (error: any) {
      console.error('Ошибка загрузки промптов:', error);
    } finally {
      setPromptsLoading(false);
    }
  };

  const savePrompts = async () => {
    try {
      setPromptsSaving(true);
      await api.put('/admin/ai-token-usage/parser-prompts', {
        extraction: extractionPrompt,
        classification: classificationPrompt
      });
      toast.success('Промпты парсера обновлены');
      setPromptsCustom({ extraction: true, classification: true });
    } catch (error: any) {
      console.error('Ошибка сохранения промптов:', error);
      toast.error('Ошибка сохранения промптов');
    } finally {
      setPromptsSaving(false);
    }
  };

  const resetPrompts = async (type: 'extraction' | 'classification' | 'all') => {
    if (!window.confirm('Сбросить промпт к значению по умолчанию?')) return;
    try {
      await api.post('/admin/ai-token-usage/parser-prompts/reset', { type });
      toast.success('Промпт сброшен к значению по умолчанию');
      await fetchPrompts();
    } catch (error: any) {
      console.error('Ошибка сброса промпта:', error);
      toast.error('Ошибка сброса промпта');
    }
  };

  const fetchParserStats = async () => {
    try {
      setParserLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (modelFilter && modelFilter !== 'all') params.append('model', modelFilter);

      const response = await api.get(`/admin/ai-token-usage/parser-stats?${params}`);
      setParserStats(response.data.stats);
    } catch (error: any) {
      console.error('Ошибка загрузки parser stats:', error);
      // Don't show toast for parser stats - might not have data yet
    } finally {
      setParserLoading(false);
    }
  };

  const renderLoading = () => (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  // ----------- Parser Analytics Section -----------
  const renderModelSelector = () => {
    const models = parserStats?.availableModels || [];
    const selectedModel = models.find(m => m.model === modelFilter);
    return (
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <label htmlFor="model-select" className="text-sm font-medium text-gray-700 whitespace-nowrap">AI Модель:</label>
            <select
              id="model-select"
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              className="block w-64 rounded-lg border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-800 shadow-sm border focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all cursor-pointer"
            >
              <option value="all">Все модели</option>
              {models.map((m) => (
                <option key={m.model} value={m.model}>
                  {m.model} ({Number(m.requests).toLocaleString()} запросов)
                </option>
              ))}
            </select>
          </div>
          {selectedModel && (
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-medium">{selectedModel.model}</span>
              <span>Первое использование: {new Date(selectedModel.first_used).toLocaleDateString('ru-RU')}</span>
              <span>|</span>
              <span>Последнее: {new Date(selectedModel.last_used).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderParserAnalytics = () => {
    if (parserLoading) return renderLoading();

    if (!parserStats || !parserStats.total.total_requests) {
      return (
        <div className="space-y-4">
          {renderModelSelector()}
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-gray-400 mb-2">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-gray-500 text-lg">Нет данных парсера за выбранный период{modelFilter !== 'all' ? ` (модель: ${modelFilter})` : ''}</p>
            <p className="text-gray-400 text-sm mt-1">Данные появятся после запуска парсера вакансий с AI</p>
          </div>
        </div>
      );
    }

    const { total, bySource, byPromptType, timeSeries, hourly, monthlyProjection, reasoningSummary, lastVacancies } = parserStats;

    // Cost rate calculations
    const totalHours = timeSeries.length > 0 ? Math.max(
      (new Date(timeSeries[timeSeries.length - 1]?.date).getTime() - new Date(timeSeries[0]?.date).getTime()) / 3600000,
      1
    ) : 1;
    const costPerHour = Number(total.total_cost_usd || 0) / Math.max(totalHours / 24, 1) * (1/24);
    const costPerDay = monthlyProjection.avg_daily_cost || Number(total.total_cost_usd || 0) / Math.max(timeSeries.length, 1);
    const costPerMonth = monthlyProjection.projected_monthly_cost;
    // Hourly rate from last 24h data
    const recentHourlyCost = hourly.length > 0
      ? hourly.reduce((s, h) => s + Number(h.cost_usd || 0), 0) / Math.max(hourly.length, 1)
      : costPerDay / 24;

    // Prepare pie chart data for extraction vs classification
    const pieData = byPromptType.map(pt => ({
      name: pt.prompt_type === 'parser_extraction' ? 'Извлечение (HTML)' : 'Классификация (текст)',
      value: Number(pt.total_tokens),
      requests: Number(pt.requests_count),
      cost: Number(pt.total_cost_usd)
    }));

    // Prepare cumulative cost time series
    let cumulativeCost = 0;
    const costTimeSeries = timeSeries.map(d => {
      cumulativeCost += Number(d.cost_usd || 0);
      return {
        date: new Date(d.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
        cost_rub: Number(d.cost_usd || 0),
        cumulative_cost: cumulativeCost,
        tokens: Number(d.total_tokens || 0),
        requests: Number(d.requests_count || 0)
      };
    });

    // Hourly data for last 24h
    const hourlyData = hourly.map(h => ({
      hour: new Date(h.hour).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
      tokens: Number(h.total_tokens || 0),
      requests: Number(h.requests_count || 0),
      cost_rub: Number(h.cost_usd || 0)
    }));

    return (
      <div className="space-y-6">
        {/* Model Selector */}
        {renderModelSelector()}

        {/* Статистика по моделям */}
        {parserStats.availableModels && parserStats.availableModels.length > 0 && (
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Статистика по моделям</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="pb-2 pr-4 font-medium">Модель</th>
                    <th className="pb-2 pr-4 text-right font-medium">Запросов</th>
                    <th className="pb-2 pr-4 text-right font-medium">Токенов</th>
                    <th className="pb-2 pr-4 text-right font-medium">Стоимость</th>
                    <th className="pb-2 font-medium">Последнее использование</th>
                  </tr>
                </thead>
                <tbody>
                  {parserStats.availableModels.map((m) => (
                    <tr key={m.model} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 pr-4 font-medium text-gray-900">{m.model}</td>
                      <td className="py-2 pr-4 text-right">{formatNum(m.requests)}</td>
                      <td className="py-2 pr-4 text-right">{formatNum(m.total_tokens)}</td>
                      <td className="py-2 pr-4 text-right">{formatRub(m.total_cost_usd)}</td>
                      <td className="py-2 text-gray-500">{m.last_used ? new Date(m.last_used).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-indigo-500">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Запросов к AI</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatNum(total.total_requests)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-blue-500">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Всего токенов</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{formatNum(total.total_tokens)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-orange-500">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Общая стоимость</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{formatRub(total.total_cost_usd)}</p>
            <p className="text-xs text-gray-400 mt-0.5">{TOKEN_PRICE_LABEL}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-green-500">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Стоимость / вакансию</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatRubPrecise(total.avg_cost_per_request)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border-l-4 border-purple-500">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Прогноз / месяц</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">{formatRub(monthlyProjection.projected_monthly_cost)}</p>
            <p className="text-xs text-gray-400 mt-0.5">~{formatNum(monthlyProjection.avg_daily_requests)}/день</p>
          </div>
        </div>

        {/* Cost Rate Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Расход / час</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatRub(recentHourlyCost)}</p>
            <p className="text-xs text-gray-400 mt-0.5">последние 24ч</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Расход / день</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{formatRub(costPerDay)}</p>
            <p className="text-xs text-gray-400 mt-0.5">средний за период</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Расход / месяц</p>
            <p className="text-xl font-bold text-orange-600 mt-1">{formatRub(costPerMonth)}</p>
            <p className="text-xs text-gray-400 mt-0.5">прогноз</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Токенов / час</p>
            <p className="text-xl font-bold text-blue-600 mt-1">
              {formatNum(hourly.length > 0
                ? hourly.reduce((s, h) => s + Number(h.total_tokens || 0), 0) / Math.max(hourly.length, 1)
                : 0
              )}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">среднее за 24ч</p>
          </div>
        </div>

        {/* Reasoning Waste Alert */}
        {reasoningSummary && Number(reasoningSummary.length_cutoff_requests || 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg shadow p-5">
            <div className="flex items-start gap-3">
              <div className="text-red-500 mt-0.5">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800">Потери на reasoning-токенах</h4>
                <p className="text-xs text-red-600 mt-1">
                  Модель gpt-5-nano использует reasoning (внутреннее рассуждение). В некоторых запросах весь лимит токенов 
                  расходуется на reasoning, и модель не генерирует полезный ответ (finish_reason: "length").
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                  <div>
                    <p className="text-xs text-red-500">Reasoning-токенов</p>
                    <p className="text-lg font-bold text-red-700">{formatNum(reasoningSummary.total_reasoning_tokens)}</p>
                    <p className="text-xs text-red-400">
                      {Number(reasoningSummary.total_all_tokens) > 0
                        ? `${((Number(reasoningSummary.total_reasoning_tokens) / Number(reasoningSummary.total_all_tokens)) * 100).toFixed(1)}% от всех`
                        : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-red-500">Потерянных запросов</p>
                    <p className="text-lg font-bold text-red-700">{formatNum(reasoningSummary.length_cutoff_requests)}</p>
                    <p className="text-xs text-red-400">
                      {Number(reasoningSummary.total_requests) > 0
                        ? `${((Number(reasoningSummary.length_cutoff_requests) / Number(reasoningSummary.total_requests)) * 100).toFixed(1)}% от всех`
                        : ''}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-red-500">Потеряно токенов</p>
                    <p className="text-lg font-bold text-red-700">{formatNum(reasoningSummary.wasted_tokens)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-red-500">Потеряно денег</p>
                    <p className="text-lg font-bold text-red-700">{formatRub(reasoningSummary.wasted_cost_rub)}</p>
                  </div>
                </div>
                <p className="text-xs text-red-500 mt-2 font-medium">
                  Рекомендация: перейти на модель без reasoning (например, gpt-4o-mini) или развернуть свою модель (NuExtract 2.0 / Qwen2.5-7B)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Source Status Badges (with last vacancy info integrated) */}
        {sourceStatus && sourceStatus.sources && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Статус парсеров</h3>
              <div className="flex gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span> Работает ({sourceStatus.summary.ok})</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block"></span> С ошибками ({sourceStatus.summary.warning})</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span> Не работает ({sourceStatus.summary.error})</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block"></span> Ожидание ({sourceStatus.summary.pending})</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {sourceStatus.sources.map((src) => {
                const colors = STATUS_COLORS[src.status] || STATUS_COLORS.pending;
                const isHovered = hoveredSource === src.source;
                // Find last vacancy info for this source
                const lvInfo = lastVacancies?.find(lv => lv.source === src.source);
                const lvLastAt = lvInfo ? new Date(lvInfo.last_vacancy_at) : null;
                const lvAgeMs = lvLastAt ? Date.now() - lvLastAt.getTime() : 0;
                const lvAgeHours = lvAgeMs / 3600000;
                const lvAgeLabel = lvLastAt ? (
                  lvAgeHours < 1 ? `${Math.round(lvAgeMs / 60000)} мин. назад` :
                  lvAgeHours < 24 ? `${Math.round(lvAgeHours)} ч. назад` :
                  `${Math.round(lvAgeHours / 24)} дн. назад`
                ) : null;
                const freshColor = !lvLastAt ? 'text-gray-400' : lvAgeHours < 2 ? 'text-green-600' : lvAgeHours < 8 ? 'text-yellow-600' : 'text-red-500';
                return (
                  <div
                    key={src.source}
                    className={`relative rounded-lg border p-3 cursor-default transition-all ${colors.bg} ${colors.border} ${isHovered ? 'ring-2 ring-indigo-300 shadow-lg z-10' : ''}`}
                    onMouseEnter={() => setHoveredSource(src.source)}
                    onMouseLeave={() => setHoveredSource(null)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2.5 h-2.5 rounded-full ${colors.dot} flex-shrink-0`}></span>
                      <span className={`text-sm font-semibold ${colors.text} truncate`}>{src.source}</span>
                    </div>
                    {lvInfo ? (
                      <div className="text-xs space-y-0.5">
                        <p className="text-gray-600">{Number(lvInfo.total_vacancies)} вакансий</p>
                        <p className={freshColor}>{lvAgeLabel}</p>
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400">нет данных</div>
                    )}

                    {/* Tooltip on hover */}
                    {isHovered && (
                      <div className="absolute left-0 top-full mt-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-4 shadow-xl z-50"
                           style={{ minWidth: '300px' }}>
                        <p className="font-semibold text-sm mb-2">{src.source}</p>
                        <div className="space-y-1.5">
                          <p>Статус: <span className={`font-semibold ${src.status === 'ok' ? 'text-green-400' : src.status === 'warning' ? 'text-yellow-400' : src.status === 'error' ? 'text-red-400' : 'text-gray-400'}`}>
                            {src.status === 'ok' ? 'Работает' : src.status === 'warning' ? 'С ошибками' : src.status === 'error' ? 'Не работает' : 'Ожидание'}
                          </span></p>

                          {/* Last vacancy info */}
                          {lvInfo && (
                            <div className="p-2 bg-gray-800 rounded">
                              <p className="text-gray-300 font-medium mb-1">Вакансии в базе:</p>
                              <p>Всего: <span className="font-semibold text-white">{Number(lvInfo.total_vacancies)}</span></p>
                              {lvLastAt && <p>Последняя: <span className={`font-semibold ${lvAgeHours < 2 ? 'text-green-400' : lvAgeHours < 8 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {lvLastAt.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </span> <span className="text-gray-400">({lvAgeLabel})</span></p>}
                            </div>
                          )}

                          {/* Parser run stats */}
                          <div className="p-2 bg-gray-800 rounded">
                            <p className="text-gray-300 font-medium mb-1">Текущая сессия:</p>
                            <p>Сохранено: <span className="text-green-400 font-semibold">{src.vacanciesSaved}</span> | Отфильтровано: {src.vacanciesFiltered}</p>
                            <p>Ошибок: <span className={src.errorCount > 0 ? 'text-red-400 font-semibold' : ''}>{src.errorCount}</span> | Запросов: {src.totalRequests}</p>
                          </div>

                          {src.lastRunAt && <p>Посл. запуск: {new Date(src.lastRunAt).toLocaleString('ru-RU')}</p>}
                          {src.lastSuccessAt && <p>Посл. успех: {new Date(src.lastSuccessAt).toLocaleString('ru-RU')}</p>}
                          {src.lastError && (
                            <div className="mt-1 p-2 bg-red-900/50 rounded text-red-300">
                              <p className="font-semibold">Ошибка ({src.lastErrorType}):</p>
                              <p className="break-words">{src.lastError.substring(0, 200)}</p>
                              {src.lastErrorAt && <p className="text-red-400 mt-1">{new Date(src.lastErrorAt).toLocaleString('ru-RU')}</p>}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Token usage by source */}
          {bySource.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Токены по источникам</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={bySource.map(s => ({
                  source: s.source,
                  tokens: Number(s.total_tokens),
                  requests: Number(s.requests_count),
                  cost: Number(s.total_cost_usd)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="source" angle={-35} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip formatter={(value: any, name: any) => {
                    if (name === 'Стоимость') return formatRub(value);
                    return formatNum(value);
                  }} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="tokens" fill="#6366f1" name="Токены" />
                  <Bar yAxisId="right" dataKey="requests" fill="#10b981" name="Запросы" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Extraction vs Classification pie */}
          {pieData.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Извлечение vs Классификация</h3>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={110}
                    dataKey="value"
                    label={({ name, percent }: any) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {pieData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => formatNum(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-4 mt-4">
                {byPromptType.map((pt, i) => (
                  <div key={pt.prompt_type} className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">
                      {pt.prompt_type === 'parser_extraction' ? 'Извлечение (HTML)' : 'Классификация'}
                    </p>
                    <p className="text-sm font-semibold">{formatNum(pt.requests_count)} запросов</p>
                    <p className="text-xs text-gray-400">~{Math.round(Number(pt.avg_tokens_per_request))} ток/запрос</p>
                    <p className="text-xs text-orange-500">{formatRub(pt.total_cost_usd)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Cost over time */}
        {costTimeSeries.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Расходы по дням</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={costTimeSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={(value: any, name: any) => {
                  if (String(name).includes('\u20BD') || String(name).includes('Стоимость') || String(name).includes('Накоп')) return formatRub(value);
                  return formatNum(value);
                }} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="cost_rub" stroke="#f59e0b" strokeWidth={2} name="Стоимость/день (\u20BD)" dot={{ r: 3 }} />
                <Line yAxisId="right" type="monotone" dataKey="cumulative_cost" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" name="Накопл. стоимость (\u20BD)" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Hourly breakdown (last 24h) */}
        {hourlyData.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Последние 24 часа (по часам)</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="requests" fill="#818cf8" name="Запросы" />
                <Bar dataKey="tokens" fill="#a78bfa" name="Токены" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Detailed table by source */}
        {bySource.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Детализация по источникам</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Источник</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Запросов</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prompt</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Completion</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reasoning</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Всего</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Среднее</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Стоимость</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bySource.map((src) => {
                    const reasoning = parserStats.reasoningStats?.find(r => r.source === src.source);
                    return (
                      <tr key={src.source} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{src.source}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatNum(src.requests_count)}</td>
                        <td className="px-4 py-3 text-sm text-purple-600 text-right">{formatNum(src.total_prompt_tokens)}</td>
                        <td className="px-4 py-3 text-sm text-green-600 text-right">{formatNum(src.total_completion_tokens)}</td>
                        <td className="px-4 py-3 text-sm text-amber-600 text-right">{formatNum(reasoning?.total_reasoning_tokens)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">{formatNum(src.total_tokens)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">{Math.round(Number(src.avg_tokens_per_request))}</td>
                        <td className="px-4 py-3 text-sm font-medium text-orange-600 text-right">{formatRub(src.total_cost_usd)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 font-semibold">
                  <tr>
                    <td className="px-4 py-3 text-sm">Итого</td>
                    <td className="px-4 py-3 text-sm text-right">{formatNum(total.total_requests)}</td>
                    <td className="px-4 py-3 text-sm text-purple-600 text-right">{formatNum(total.total_prompt_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-green-600 text-right">{formatNum(total.total_completion_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-amber-600 text-right">
                      {formatNum(parserStats.reasoningStats?.reduce((s, r) => s + Number(r.total_reasoning_tokens || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">{formatNum(total.total_tokens)}</td>
                    <td className="px-4 py-3 text-sm text-right">{Math.round(Number(total.avg_tokens_per_request))}</td>
                    <td className="px-4 py-3 text-sm text-orange-600 text-right">{formatRub(total.total_cost_usd)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

      </div>
    );
  };

  // ----------- Prompts Editor Section -----------
  const renderPromptsEditor = () => {
    if (promptsLoading) return renderLoading();

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            Здесь вы можете редактировать системные промпты, которые используются AI-моделью для парсинга вакансий.
            Изменения применяются сразу после сохранения. Для сброса к значениям по умолчанию нажмите кнопку "Сбросить".
          </p>
        </div>

        {/* Extraction Prompt */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Промпт извлечения данных (Extraction)</h3>
              <p className="text-xs text-gray-500 mt-1">Используется для извлечения структурированных данных из HTML-страницы вакансии</p>
            </div>
            <div className="flex gap-2">
              {promptsCustom.extraction && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Кастомный</span>
              )}
              <button
                onClick={() => resetPrompts('extraction')}
                className="text-xs text-red-600 hover:text-red-800 px-3 py-1 border border-red-200 rounded hover:bg-red-50"
              >
                Сбросить
              </button>
            </div>
          </div>
          <textarea
            value={extractionPrompt}
            onChange={(e) => setExtractionPrompt(e.target.value)}
            className="w-full h-96 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Системный промпт для извлечения данных..."
          />
          <p className="text-xs text-gray-400 mt-1">{extractionPrompt.length} символов</p>
        </div>

        {/* Classification Prompt */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Промпт классификации (Classification)</h3>
              <p className="text-xs text-gray-500 mt-1">Используется для классификации уже спарсенных вакансий (без HTML)</p>
            </div>
            <div className="flex gap-2">
              {promptsCustom.classification && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Кастомный</span>
              )}
              <button
                onClick={() => resetPrompts('classification')}
                className="text-xs text-red-600 hover:text-red-800 px-3 py-1 border border-red-200 rounded hover:bg-red-50"
              >
                Сбросить
              </button>
            </div>
          </div>
          <textarea
            value={classificationPrompt}
            onChange={(e) => setClassificationPrompt(e.target.value)}
            className="w-full h-72 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Системный промпт для классификации..."
          />
          <p className="text-xs text-gray-400 mt-1">{classificationPrompt.length} символов</p>
        </div>

        {/* Save button */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => resetPrompts('all')}
            className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
          >
            Сбросить все к умолчанию
          </button>
          <button
            onClick={savePrompts}
            disabled={promptsSaving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
          >
            {promptsSaving ? 'Сохранение...' : 'Сохранить промпты'}
          </button>
        </div>
      </div>
    );
  };

  // ----------- User AI Analytics Section -----------
  const renderUserAnalytics = () => {
    if (loading) return renderLoading();

    if (!stats) {
      return (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Ошибка загрузки данных
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Общая статистика */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-2">Всего запросов</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total.total_requests || 0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-2">Всего токенов</p>
            <p className="text-2xl font-bold text-blue-600">{formatNum(stats.total.total_tokens)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-2">Токенов в промптах</p>
            <p className="text-2xl font-bold text-purple-600">{formatNum(stats.total.total_prompt_tokens)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-2">Токенов в ответах</p>
            <p className="text-2xl font-bold text-green-600">{formatNum(stats.total.total_completion_tokens)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-2">Стоимость (USD)</p>
            <p className="text-2xl font-bold text-orange-600">${Number(stats.total.total_cost_usd || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* График по типам промптов */}
        {stats.byPromptType && stats.byPromptType.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Использование по типам промптов</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.byPromptType}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="prompt_type"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="total_tokens" fill="#8884d8" name="Токены" />
                <Bar dataKey="requests_count" fill="#82ca9d" name="Запросы" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Таблица по пользователям */}
        {stats.byUser && stats.byUser.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Топ пользователей по использованию токенов</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Пользователь</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Запросов</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Всего токенов</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Среднее на запрос</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Стоимость</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.byUser.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.requests_count}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNum(user.total_tokens)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Math.round(user.avg_tokens_per_request || 0)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${Number(user.total_cost || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(!stats.byPromptType || stats.byPromptType.length === 0) && (!stats.byUser || stats.byUser.length === 0) && (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-500">Нет данных за выбранный период</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Аналитика токенов AI</h1>
          <p className="text-gray-600 mt-2">Статистика использования AI для парсера вакансий и пользовательских запросов</p>
        </div>
        <div className="flex gap-4">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Начальная дата"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Конечная дата"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('parser')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'parser'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Парсер вакансий (AI)
          </button>
          <button
            onClick={() => setActiveTab('user')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'user'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Пользовательские AI запросы
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'prompts'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Системные промпты парсера
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'parser' ? renderParserAnalytics() : activeTab === 'prompts' ? renderPromptsEditor() : renderUserAnalytics()}
    </div>
  );
};

export default AITokenAnalyticsPage;
